import React, { useState } from "react";
import styled from "styled-components";
import CompanyCard from "./CompanyCard";
import Hero from "./Hero";
import { Alert } from "@mui/material";
import useOrganizationReport from "src/swr/hooks/RetailReports/useOrganizationReport";
import { Box } from "react-feather";
import PrivateHeader from "src/components/PrivateHeader";
import { Helmet } from "react-helmet-async";
import LoaderWrapper from "src/components/Loaders/LoaderWrapper";
import ErrorBlockWithPermissions from "src/components/UI/ErrorBlockWithPermissions";
import useGlobalStore from "src/store/useGlobalStore";

const Wrapper = styled.div`
  min-height: 100%;
  width: 100%;
  padding: 0px;

  @media (max-width: 600px) {
    padding: 10px;
  }
`;

const GrayWrapper = styled.div`
  width: 100%;
  background: #eeeeee;
  padding: 20px;
  border-radius: 12px;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const Spacer = styled.div`
  height: 1px;
  width: 100%;
  background-color: lightgray;
  margin-bottom: 24px;
  margin-top: 24px;
`;
const CompanyListContainer = styled.div`
  width: 100%;
  min-height: 100%;
  gap: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  padding-top: 10px;
`;

const OrganizationReport = () => {
  const setSnackbarText = useGlobalStore((state) => state.setSnackbarText);
  const config = useGlobalStore((state) => state.config);
  const { data, isLoading, isError } = useOrganizationReport();

  if (isLoading) {
    return (
      <>
        <Helmet title="Spiffy | Organization" />
        <PrivateHeader header="Organization Report" Icon={Box} />
        <LoaderWrapper text="Organization Report" />
      </>
    );
  }
  if (isError) {
    return (
      <>
        <PrivateHeader header="Organization Report" Icon={Box} />
        <ErrorBlockWithPermissions errorObj={isError} />
      </>
    );
  }

  const retailCompaniesOnly = data?.company_data
    ?.filter((c) => c.company_type === "retail")
    .map((company) => ({
      ...company,
      image: config.available_companies.find(
        (c) => c.company_id === company.company_id,
      ).company_logo,
    }));

  // Sum all overall team members
  const totalTeamMembers = retailCompaniesOnly.reduce(
    (sum, company) => sum + (company?.data?.team_members?.overall || 0),
    0,
  );

  // Sum all submissions
  const totalSubmissions = retailCompaniesOnly.reduce(
    (sum, company) => sum + (company?.data?.submissions?.submissions || 0),
    0,
  );

  const totalCompletions = retailCompaniesOnly.reduce(
    (sum, company) => sum + (company?.data?.submissions?.completions || 0),
    0,
  );

  const totalLiveModules = retailCompaniesOnly.reduce(
    (sum, company) => sum + company.data.live_modules,
    0,
  );

  const totalRecentlyActiveTeamMembers = retailCompaniesOnly.reduce(
    (sum, company) => sum + company.data.team_members?.recently_active,
    0,
  );

  const totalRecentSubmissions = retailCompaniesOnly.reduce(
    (sum, company) =>
      sum + (company?.data?.submissions?.recent_submissions || 0),
    0,
  );

  const totalRecentCompletions = retailCompaniesOnly.reduce(
    (sum, company) =>
      sum + (company?.data?.submissions?.recent_completions || 0),
    0,
  );

  const totals = {
    totalRecentCompletions,
    totalRecentSubmissions,
    totalRecentlyActiveTeamMembers,
    totalLiveModules,
    totalCompletions,
    totalSubmissions,
    totalTeamMembers,
  };

  return (
    <>
      <PrivateHeader header="Organization Report" Icon={Box} />
      <Wrapper>
        <GrayWrapper>
          <Hero
            name={data?.organization_name}
            companyArray={retailCompaniesOnly}
            totals={totals}
          />
          <Alert
            severity="info"
            sx={{
              width: "100%",
              mt: 4,
              border: "1px solid lightgray",
              background: "#faf9f6",
            }}
          >
            <b>Statistics Update Once Per Day</b>
          </Alert>
          <Spacer />
          <p
            style={{
              fontSize: 14,
              fontWeight: 600,
              marginLeft: 4,
              marginBottom: 6,
            }}
          >
            Companies in organization
          </p>
          <CompanyListContainer>
            {retailCompaniesOnly.map((company) => (
              <CompanyCard key={company?.company_id} company={company} />
            ))}
          </CompanyListContainer>
        </GrayWrapper>
        <div style={{ minHeight: 40 }} />
      </Wrapper>
    </>
  );
};

export default OrganizationReport;
